import { useRef, useState, useEffect } from "react";
import { actionPhotos } from "@/mocks/productsCdbjjLP";

const PHG_LOGO = "/images/phg-logo.png";
const CDBJJ_LOGO = "/images/carpe2.png";

function useReveal(threshold = 0.06) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, visible };
}

export default function CdbjjBrand() {
  const { ref: topRef, visible: topVisible } = useReveal(0.06);
  const { ref: cdbRef, visible: cdbVisible } = useReveal(0.08);
  const { ref: phgRef, visible: phgVisible } = useReveal(0.08);
  const { ref: statsRef, visible: statsVisible } = useReveal(0.1);

  return (
    <section className="w-full bg-black overflow-hidden">

      {/* ── COLLAB INTRO ── */}
      <div
        ref={topRef}
        className="relative w-full"
        style={{ height: "65vh" }}
      >
        <img
          src={actionPhotos[19]}
          alt="CDBJJ × PHG"
          className={`w-full h-full object-cover transition-all duration-1200 ${topVisible ? "scale-100 opacity-100" : "scale-105 opacity-0"}`}
          style={{ objectPosition: "85% center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        <div className={`absolute inset-0 flex flex-col justify-center px-6 md:pl-20 transition-all duration-700 delay-300 ${topVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"}`}>
          <p className="text-red-500 text-sm tracking-[0.4em] uppercase mb-4 md:mb-6">About the Collab</p>

          {/* ロゴ — CDBJJ先頭 */}
          <div className="flex flex-col gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="flex items-center gap-3 md:gap-5">
              <div className="w-[55px] h-[55px] md:w-[110px] md:h-[110px] flex items-center justify-center flex-shrink-0">
                <img src={CDBJJ_LOGO} alt="Carpe Diem BJJ" className="w-full h-full object-contain" />
              </div>
              <h2
                className="text-white font-black uppercase leading-none"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(18px, 3.5vw, 52px)", letterSpacing: "0.03em" }}
              >
                CARPE DIEM BJJ
              </h2>
            </div>
            <div className="flex items-center py-2">
              <div className="relative w-20 h-20 md:w-28 md:h-28 ml-4 md:ml-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-px bg-white/25 rotate-45" style={{ transformOrigin: "center" }} />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-px bg-white/25 -rotate-45" style={{ transformOrigin: "center" }} />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 md:gap-5">
              <div className="w-[60px] h-[60px] md:w-[120px] md:h-[120px] flex items-center justify-center flex-shrink-0">
                <img src={PHG_LOGO} alt="Powerhouse Gym" className="w-full h-full object-contain" />
              </div>
              <h2
                className="text-white font-black uppercase leading-none"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(18px, 3.5vw, 52px)", letterSpacing: "0.03em" }}
              >
                POWERHOUSE GYM
              </h2>
            </div>
          </div>

          <div className="w-12 h-px bg-red-600 mb-4 md:mb-6" />
          <p className="text-white/70 text-sm md:text-base leading-loose max-w-md">
            知性を磨く柔術と、世界基準の情熱が交わる場所。<br />
            二つの伝説が共鳴し、生まれたコレクション。
          </p>
        </div>
      </div>

      {/* ── CDBJJ BLOCK ── */}
      <div
        ref={cdbRef}
        className={`w-full bg-black border-t border-white/5 transition-all duration-800 ${cdbVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      >
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row">

          {/* 左: 番号 + ブランド名 */}
          <div className="w-full lg:w-[38%] px-6 md:px-16 py-10 md:py-16 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-white/5">
            <div>
              <span className="text-white/5 font-black select-none block leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(80px, 10vw, 130px)" }}>01</span>
              <p className="text-red-600 text-sm tracking-[0.3em] uppercase mb-4">Brazilian Jiu-Jitsu</p>
              <div className="flex items-center gap-4 mb-3">
                <div className="w-[110px] h-[110px] flex items-center justify-center flex-shrink-0">
                  <img src={CDBJJ_LOGO} alt="Carpe Diem BJJ" className="w-full h-full object-contain" />
                </div>
                <h3
                  className="text-white font-black uppercase leading-tight"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(20px, 2.5vw, 34px)", letterSpacing: "0.04em" }}
                >
                  CARPE DIEM<br />BJJ
                </h3>
              </div>
              <p className="text-red-400 text-sm tracking-widest mt-3 italic">
                more than jiu-jitsu
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {["青山・自由が丘", "日本全国・世界展開", "more than jiu-jitsu"].map((tag) => (
                <span key={tag} className="text-white/60 text-xs border border-white/20 px-3 py-1 tracking-wide">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* 右: 説明文 */}
          <div className="w-full lg:flex-1 px-6 md:px-16 py-10 md:py-16 flex flex-col justify-center">
            <p className="text-white/75 text-base leading-loose mb-6 max-w-lg">
              格闘技の中のチェスとも謳われるブラジリアン柔術。「カルペ・ディエム（今を生きる）」を掲げ、
              格闘技の常識を覆すモダンな柔術道場。
            </p>
            <p className="text-white/55 text-base leading-loose max-w-lg">
              青山・自由が丘を始め日本全国、そして世界にも拠点を持つ。高い技術と都会的なデザインを融合させ、
              多忙な現代人が知性を磨き、仲間と高め合うためのBJJ GYMです。
            </p>
            <div className="mt-8 w-8 h-px bg-red-600/50" />
          </div>
        </div>
      </div>

      {/* ── PHG BLOCK ── */}
      <div
        ref={phgRef}
        className={`w-full bg-[#060606] border-t border-white/5 transition-all duration-800 delay-100 ${phgVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
      >
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row-reverse">

          {/* 右: 番号 + ブランド名 */}
          <div className="w-full lg:w-[38%] px-6 md:px-16 py-10 md:py-16 flex flex-col justify-between border-b lg:border-b-0 lg:border-l border-white/5">
            <div>
              <span className="text-white/5 font-black select-none block leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(80px, 10vw, 130px)" }}>02</span>
              <p className="text-red-600 text-sm tracking-[0.3em] uppercase mb-4">Fitness Gym</p>
              <div className="flex items-center gap-4 mb-3">
                <div className="w-[120px] h-[120px] flex items-center justify-center flex-shrink-0">
                  <img src={PHG_LOGO} alt="Powerhouse Gym" className="w-full h-full object-contain" />
                </div>
                <h3
                  className="text-white font-black uppercase leading-tight"
                  style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(20px, 2.5vw, 34px)", letterSpacing: "0.04em" }}
                >
                  POWERHOUSE<br />GYM
                </h3>
              </div>
              <p className="text-red-400 text-sm tracking-widest mt-3 italic">
                世界基準の情熱が、肉体を変える。
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {["1974年米国誕生", "世界最大級", "東京・大阪・さいたま 全国展開"].map((tag) => (
                <span key={tag} className="text-white/60 text-xs border border-white/20 px-3 py-1 tracking-wide">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* 左: 説明文 */}
          <div className="w-full lg:flex-1 px-6 md:px-16 py-10 md:py-16 flex flex-col justify-center">
            <p className="text-white/75 text-base leading-loose mb-6 max-w-lg">
              1974年、米国に誕生した世界最大級の伝説的ジム。
              山岸秀匡らトッププロが集う「聖地」として、東京・大阪・さいたまをはじめ全国で展開。
            </p>
            <p className="text-white/55 text-base leading-loose max-w-lg">
              最高峰のマシンと圧倒的な熱量を提供し、初心者からトップアスリートまで、
              本物を求めるすべての人のための鋼の神殿です。
            </p>
            <div className="mt-8 w-8 h-px bg-red-600/50" />
          </div>
        </div>
      </div>

      {/* ── STATS BAR ── */}
      <div
        ref={statsRef}
        className={`w-full bg-[#040404] border-t border-white/5 transition-all duration-700 delay-200 ${statsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
      >
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-stretch divide-y sm:divide-y-0 sm:divide-x divide-white/5">
          {[
            { num: "04", label: "ITEMS", sub: "Long Sleeve / Half Sleeve / Shorts / Tee" },
            { num: "02", label: "COLORS", sub: "BLACK & GRAY" },
            { num: "2026", label: "COLLECTION", sub: "Live the Discipline Active" },
            { num: "CDBJJ×PHG", label: "COLLAB", sub: "Carpe Diem BJJ × Powerhouse Gym" },
          ].map((item) => (
            <div key={item.label} className="flex-1 px-8 py-7">
              <p
                className="text-white font-black leading-none mb-1"
                style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: "clamp(20px, 2.5vw, 32px)", letterSpacing: "0.04em" }}
              >
                {item.num}
              </p>
              <p className="text-red-600 text-xs tracking-[0.3em] uppercase mb-1">{item.label}</p>
              <p className="text-white/50 text-sm leading-relaxed">{item.sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
